import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LucideIconService {
  private iconCache: { [key: string]: string } = {};
  private loadedIcons: Set<string> = new Set();

  /**
   * Carga un icono de Lucide dinámicamente
   * @param iconName Nombre del icono
   * @returns Promise con el SVG del icono
   */
  async loadIcon(iconName: string): Promise<string> {
    // Verificar si ya está en caché
    if (this.iconCache[iconName]) {
      return this.iconCache[iconName];
    }

    // Verificar si ya se está cargando
    if (this.loadedIcons.has(iconName)) {
      return this.waitForIcon(iconName);
    }

    this.loadedIcons.add(iconName);

    try {
      // Usar Iconify API para obtener el SVG
      const response = await fetch(`https://api.iconify.design/lucide/${iconName}.svg`);

      if (!response.ok) {
        throw new Error(`Icono ${iconName} no encontrado`);
      }

      const svgContent = await response.text();
      this.iconCache[iconName] = svgContent;
      this.loadedIcons.delete(iconName);

      return svgContent;
    } catch (error) {
      console.warn(`Error cargando icono ${iconName}:`, error);
      this.loadedIcons.delete(iconName);
      return this.getFallbackIcon(iconName);
    }
  }

  /**
   * Espera a que un icono termine de cargar
   */
  private async waitForIcon(iconName: string): Promise<string> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.iconCache[iconName]) {
          clearInterval(checkInterval);
          resolve(this.iconCache[iconName]);
        }
      }, 50);
    });
  }

  /**
   * Obtiene un icono de fallback
   */
  private getFallbackIcon(iconName: string): string {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2"/>
        <text x="12" y="16" text-anchor="middle" font-size="8" fill="currentColor">?</text>
      </svg>
    `;
  }

  /**
   * Pre-carga iconos comunes
   */
  async preloadCommonIcons(): Promise<void> {
    const commonIcons = [
      'home', 'user', 'settings', 'search', 'star', 'heart', 'mail', 'phone',
      'calendar', 'clock', 'map-pin', 'link', 'download', 'upload', 'file',
      'folder', 'image', 'video', 'music', 'play', 'pause', 'volume',
      'wifi', 'bluetooth', 'battery', 'signal', 'sun', 'moon', 'cloud',
      'shopping-cart', 'credit-card', 'dollar-sign', 'github', 'twitter',
      'facebook', 'instagram', 'linkedin', 'youtube', 'loader', 'loader-circle', 'panel-left', 'panel-right'
    ];

    const loadPromises = commonIcons.map(icon => this.loadIcon(icon));
    await Promise.allSettled(loadPromises);
  }

  /**
   * Verifica si un icono está disponible
   */
  isIconAvailable(iconName: string): boolean {
    return this.iconCache.hasOwnProperty(iconName);
  }

  /**
   * Obtiene la lista de iconos cargados
   */
  getLoadedIcons(): string[] {
    return Object.keys(this.iconCache);
  }

  /**
   * Limpia el caché de iconos
   */
  clearCache(): void {
    this.iconCache = {};
    this.loadedIcons.clear();
  }
}

